import {
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useBilling = (userID, interval) => {
  const { api } = useContext(GlobalContext);

  const [walletBalance, setWalletBalance] = useState(0);
  const [billingData, setBillingData] = useState({});
  const [ledgerData, setLedgerData] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(false);

  /* ================= DATE STATE ================= */

  const getCurrentDate = () =>
    new Date().toISOString().split("T")[0];

  const getOneDayBackDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);

    return date.toISOString().split("T")[0];
  };

  const [startDateCurrent, setStartDateCurrent] = useState(
    getOneDayBackDate,
  );

  const [endDateCurrent, setEndDateCurrent] = useState(
    getCurrentDate,
  );

  /* ================= COMMON POST ================= */

  const postRequest = useCallback(
    async (endpoint, body) => {
      if (!api) {
        console.error("Billing API instance not found");
        return null;
      }

      const response = await api.post(endpoint, body);

      return response?.data;
    },
    [api],
  );

  /* ================= API CALLS ================= */

  const fetchWalletBalance = useCallback(async () => {
    if (!userID) return null;

    try {
      const data = await postRequest(
        "/Whatsapp/v1.0/getParentWallet",
        {
          userid: userID,
          secret: "string",
        },
      );

      if (data?.responseCode === "00") {
        setWalletBalance(
          Number(data?.data?.wl_balance || 0),
        );
      } else {
        setWalletBalance(0);
      }

      return data;
    } catch (error) {
      console.error(
        "Wallet API Error:",
        error?.response?.data || error,
      );

      setWalletBalance(0);

      return null;
    }
  }, [userID, postRequest]);

  const fetchBillingData = useCallback(async () => {
    if (!userID) return null;

    try {
      const data = await postRequest(
        "/Whatsapp/v1/getBillingData",
        {
          userid: userID,
          interval,
          secret: "string",
        },
      );

      if (data?.responseCode === "00") {
        setBillingData(data?.data || {});
      } else {
        setBillingData({});
      }

      return data;
    } catch (error) {
      console.error(
        "Billing API Error:",
        error?.response?.data || error,
      );

      setBillingData({});

      return null;
    }
  }, [userID, interval, postRequest]);

  const fetchLedgerData = useCallback(async () => {
    if (!userID) return null;

    try {
      setLoading(true);

      const data = await postRequest(
        "/Whatsapp/getBillingLedger",
        {
          userid: userID,
          fromDate: startDateCurrent,
          toDate: endDateCurrent,
        },
      );

      if (data?.responseCode === "00") {
        const responseData = Array.isArray(data?.data)
          ? data.data
          : [];

        setLedgerData(responseData);
      } else {
        setLedgerData([]);
      }

      return data;
    } catch (error) {
      console.error(
        "Ledger API Error:",
        error?.response?.data || error,
      );

      setLedgerData([]);

      return null;
    } finally {
      setLoading(false);
    }
  }, [
    userID,
    startDateCurrent,
    endDateCurrent,
    postRequest,
  ]);

  const fetchInsights = useCallback(async () => {
    if (!userID) return null;

    try {
      setLoading(true);

      const data = await postRequest(
        "/Whatsapp/whatsappInsights",
        {
          userid: userID,
          secret: "string",
        },
      );

      if (data?.responseCode === "00") {
        setInsights(data?.data || {});
      } else {
        setInsights({});
      }

      return data;
    } catch (error) {
      console.error(
        "Insights API Error:",
        error?.response?.data || error,
      );

      setInsights({});

      return null;
    } finally {
      setLoading(false);
    }
  }, [userID, postRequest]);

  /* ================= EFFECTS ================= */

  useEffect(() => {
    if (!userID || !api) return;

    fetchWalletBalance();
    fetchInsights();
  }, [
    userID,
    api,
    fetchWalletBalance,
    fetchInsights,
  ]);

  useEffect(() => {
    if (!userID || !api) return;

    fetchBillingData();
  }, [userID, interval, api, fetchBillingData]);

  useEffect(() => {
    if (!userID || !api) return;

    fetchLedgerData();
  }, [
    userID,
    startDateCurrent,
    endDateCurrent,
    api,
    fetchLedgerData,
  ]);

  return {
    walletBalance,
    billingData,
    ledgerData,
    insights,
    loading,

    startDateCurrent,
    endDateCurrent,
    setStartDateCurrent,
    setEndDateCurrent,

    fetchWalletBalance,
    fetchBillingData,
    fetchLedgerData,
    fetchInsights,
  };
};

export default useBilling;